import {OnboardingStyle} from "@components/onboarding/onboarding.style";
import {createStyles, Theme} from "@material-ui/core";
import {secondaryColor, styleColors} from "src/main.style";

export const subscriptionStyle = (theme: Theme) => createStyles({
    sub: {
      position: 'relative',
      margin: '24px 0px 0px 0px',
      padding: '20px 22px',
      borderRadius: '16px',
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      overflow: 'hidden',
      wordBreak: 'break-word',
    },
    subActive: {
      color: styleColors.premiumText,
      background: `linear-gradient(135deg, ${styleColors.premiumStart} 0%, ${styleColors.premiumMiddle} 55%, ${styleColors.premiumEnd} 100%)`,
      boxShadow: `0 12px 28px -10px ${styleColors.premiumGlow}, 0 4px 10px ${styleColors.blackAlpha12}`,
      // A slow light sweep across the card
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: '-60%',
        width: '50%',
        background: `linear-gradient(110deg, ${styleColors.transparent}, ${styleColors.whiteOverlay28}, ${styleColors.transparent})`,
        transform: 'skewX(-20deg)',
        animation: '$premiumShine 6s ease-in-out infinite',
        pointerEvents: 'none',
      },
      '@media (prefers-reduced-motion: reduce)': {
        '&::before': { animation: 'none', display: 'none' },
      },
    },
    subExpired: {
      color: styleColors.greyDark1,
      background: styleColors.greyLight3,
      border: `1px solid ${styleColors.greyMedium1}`,
    },
    subLabel: {
      fontWeight: 400,
      fontSize: '14px',
      margin: 0,
      opacity: 0.85,
    },
    subAccess: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontWeight: 600,
      fontSize: '16px',
      margin: 0,
    },
    liveDot: {
      position: 'relative',
      flexShrink: 0,
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      background: styleColors.callGreen,
      '&::after': {
        content: '""',
        position: 'absolute',
        inset: 0,
        borderRadius: '50%',
        background: styleColors.callGreen,
        animation: '$livePulse 2s ease-out infinite',
      },
      '@media (prefers-reduced-motion: reduce)': {
        '&::after': { animation: 'none' },
      },
    },
    expiredDot: {
      flexShrink: 0,
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      background: styleColors.greyMedium3,
    },
    subName: {
      fontWeight: 700,
      fontSize: '24px',
      lineHeight: 1.25,
      margin: 0,
    },
    subDivider: {
      height: '1px',
      border: 0,
      margin: 0,
      background: 'currentColor',
      opacity: 0.2,
    },
    subTimeLeft: {
      fontWeight: 700,
      fontSize: '28px',
      lineHeight: 1.2,
      margin: '4px 0 0 0',
    },
    subManage: {
      fontWeight: 400,
      fontSize: '13px',
      margin: 0,
      opacity: 0.8,
    },

    imageContent: {
      overflow:'hidden',
      width: '100%',
      pointerEvents: 'none',
      height: 'min(68%, calc(100% - 210px))',
      position: 'absolute',
      top: '0px',
      right: '0px',
    },
    halfImage : {
      height: 'auto',
      width: '150px',
      position: 'absolute',
      bottom: 0,
      right: 0,
      transform: 'translateX(50%)',
    },
    bottomContent: {
        ...OnboardingStyle(theme).bottomContent,
    },
    offer : {
      marginTop: '60px',
    },
    alternatives : {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center',
      gap: '12px',
    },
    freePeriod : {
      textAlign: 'center',
      marginTop: '12px',
      fontSize: '16px',
      fontWeight: 500,
    },
    alternative : {
      display: 'flex',
      flexDirection: 'column',
      border: '3px solid '+secondaryColor,
      padding: '20px',
      borderRadius: '10px',
      gap: '6px',
    },
    period : {
      alignSelf: 'center',
      fontSize: '14px',
      fontWeight: 600,
    },
    periodLength : {
      alignSelf: 'center',
      fontSize: '24px',
      fontWeight: 600,
    },
    periodPrice : {
      alignSelf: 'center',
      fontSize: '24px',
      fontWeight: 600,
    },
    iconStyled : {
      color:styleColors.white, 
      backgroundColor:styleColors.deepPurple, 
      borderRadius:50,
      fontSize: '20px',
      padding: '4px',
      marginRight: '12px',
    },
    eventBox : {
      display:'flex', 
      flexDirection:'row',
      marginBottom:'12px',
      zIndex:1
    },
    eventDetails : {
      fontSize:'12px',
      maxWidth:'80%',
    },
    testimonial : {
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
      marginTop: '48px',
      alignItems: 'center',
    },
        /* -------------------------------- KEYFRAMES ---------------------------- */
    '@keyframes premiumShine': {
      '0%': { left: '-60%' },
      '60%': { left: '130%' },
      '100%': { left: '130%' },
    },
    '@keyframes livePulse': {
      '0%': { transform: 'scale(1)', opacity: 0.7 },
      '100%': { transform: 'scale(2.8)', opacity: 0 },
    },
    '@global': {
      /* straight up */
      '@keyframes sparkUp': {
        '0%':  { transform: 'translate(0, 0)   scale(.9)', opacity: 1 },
        '70%': { opacity: 1 },
        '100%':{ transform: 'translate(0, -90px) scale(.3)', opacity: 0 },
      },
      /* up-and-left */
      '@keyframes sparkUpLeft': {
        '0%':  { transform: 'translate(0, 0)   scale(.9)', opacity: 1 },
        '70%': { opacity: 1 },
        '100%':{ transform: 'translate(-20px, -90px) scale(.3)', opacity: 0 },
      },
      /* up-and-right */
      '@keyframes sparkUpRight': {
        '0%':  { transform: 'translate(0, 0)   scale(.9)', opacity: 1 },
        '70%': { opacity: 1 },
        '100%':{ transform: 'translate(20px, -90px)  scale(.3)', opacity: 0 },
      },
    },

    confetti: {
      pointerEvents: 'none',
      position: 'absolute',
      left: '50%',                // anchor in the middle
      transform: 'translateX(-50%)',
      width: 0,
      bottom: 70,
      height: 0,
      overflow: 'visible',
      zIndex: 2,

      /* base particle */
      '& span': {
        position: 'absolute',
        bottom: 0,
        width: 6,
        height: 6,
        borderRadius: '50%',
        background: theme.palette.primary.main,
        opacity: 0,
        animationDuration: '900ms',
        animationTimingFunction: 'ease-out',
        animationFillMode: 'forwards',
      },

      /* colour tweaks */
      '& span:nth-child(odd)': { background: theme.palette.secondary.main },
      '& span:nth-child(3n)':  { background: styleColors.white },

      /* 5 particles: two left, one centre, two right */
      '& span:nth-child(1)': {
        left: '-60px',
        animationName: '$sparkUpLeft',
        animationDelay: '0ms',
      },
      '& span:nth-child(2)': {
        left: '-30px',
        animationName: '$sparkUpLeft',
        animationDelay: '80ms',
      },
      '& span:nth-child(3)': {
        left: '0px',
        animationName: '$sparkUp',
        animationDelay: '160ms',
      },
      '& span:nth-child(4)': {
        left: '30px',
        animationName: '$sparkUpRight',
        animationDelay: '240ms',
      },
      '& span:nth-child(5)': {
        left: '60px',
        animationName: '$sparkUpRight',
        animationDelay: '320ms',
      },
    },

});



// WEBPACK FOOTER //
// ./src/components/subscription/subscription.style.ts